import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { DetailRowsSkeleton } from '@/components/core';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { Label } from '@/components/ui-kit/label';
import { Card } from '@/components/ui-kit/card';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { DEFAULT_TIMESHEETS_ORDER } from '../../graphql/queries';
import { createTimesheetEntriesTableColumns } from '../../components/timesheet-entries-table/timesheet-entries-table-columns';
import { TimesheetBreakdownTable } from '../../components/timesheet-breakdown-table/timesheet-breakdown-table';
import {
  useGetTimeSheetEntries,
  useGetUsersTimesheetRollup,
} from '../../hooks/use-timesheet-manager';
import { ResourceBreakdown, TimeSheetsPagingResult } from '../../types/timesheet-manager.types';
import { orgToday, startOfMonthDay, taskDateRangeClause } from '../../../utils/task-date';

export const UserTimesheetDetailPage = () => {
  const { t } = useTranslation();
  const { userId = '' } = useParams<{ userId: string }>();

  const [dateRange, setDateRange] = useState(() => {
    const today = orgToday();
    return { from: startOfMonthDay(today), to: today };
  });
  const { pagination, resetPage, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_TIMESHEETS_ORDER,
  });

  // A new date range starts the entries over from the first page.
  useEffect(() => {
    resetPage();
  }, [dateRange, resetPage]);

  const { data: rollupData, isLoading: isRollupLoading } = useGetUsersTimesheetRollup({
    startDate: dateRange.from,
    endDate: dateRange.to,
    userId,
  });
  const resource = ((rollupData as ResourceBreakdown[]) ?? [])[0];

  const entriesFilter = {
    UserId: userId,
    TaskDate: taskDateRangeClause(dateRange.from, dateRange.to),
  };
  const {
    data: entriesData,
    isLoading: isEntriesLoading,
    error,
  } = useGetTimeSheetEntries({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere(entriesFilter, columnWhere),
    order,
  });
  const entriesResult = (entriesData as { TimeSheets?: TimeSheetsPagingResult })?.TimeSheets;
  const entries = entriesResult?.items ?? [];
  const totalCount = entriesResult?.totalCount ?? 0;

  const columns = createTimesheetEntriesTableColumns({ t });

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          {/* The employee's name and department come with the breakdown: placeholders until then
              rather than the generic title. */}
          {isRollupLoading && !resource ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-32" />
            </div>
          ) : (
            <>
              <h1 className="break-words text-lg font-semibold">
                {resource?.ResourceName || t('TIMESHEET_MANAGER_TIMESHEET_DETAIL')}
              </h1>
              {resource?.DepartmentName && (
                <p className="text-sm text-neutral-500">{resource.DepartmentName}</p>
              )}
            </>
          )}
        </div>
        <div className="grid w-full grid-cols-1 gap-3 min-[360px]:grid-cols-2 sm:flex sm:w-auto sm:items-center">
          <div className="space-y-2">
            <Label htmlFor="user-timesheet-start">{t('START_DATE')}</Label>
            <DatePicker
              id="user-timesheet-start"
              value={dateRange.from}
              onChange={(value) => setDateRange((prev) => ({ ...prev, from: value }))}
              className="w-full sm:w-40"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="user-timesheet-end">{t('END_DATE')}</Label>
            <DatePicker
              id="user-timesheet-end"
              value={dateRange.to}
              onChange={(value) => setDateRange((prev) => ({ ...prev, to: value }))}
              className="w-full sm:w-40"
            />
          </div>
        </div>
      </div>

      <Card className="p-4">
        <h2 className="mb-3 text-sm font-semibold text-neutral-700">
          {t('TIMESHEET_MANAGER_CUSTOMER_PROJECT_BREAKDOWN')}
        </h2>
        {isRollupLoading ? (
          <DetailRowsSkeleton rows={4} />
        ) : (
          <TimesheetBreakdownTable
            resource={
              resource ?? {
                UserId: userId,
                ResourceName: '',
                Customers: [],
                GrandTotal: 0,
              }
            }
          />
        )}
      </Card>

      <div>
        <h2 className="mb-3 text-sm font-semibold text-neutral-700">
          {t('TIMESHEET_MANAGER_DAILY_ENTRIES')}
        </h2>
        <AdvanceDataTable
          {...tableProps}
          data={entries}
          columns={columns}
          isLoading={isEntriesLoading}
          error={error instanceof Error ? error : null}
          pagination={{
            pageIndex: pagination.pageIndex,
            pageSize: pagination.pageSize,
            totalCount,
          }}
          isExpandRowContent={false}
          mobileView="cards"
        />
      </div>
    </div>
  );
};
